import type { AddressConfidenceNote } from '../composites/addressConfidence';
import { ZOHO_BOOKS_IMPORT_PATH } from './importRun';

/**
 * The outside-call log (piece 31; decisions 5720, 5880 and 7370, T2800, T2828
 * and T3061): one `OutsideCallLog` row per call the application makes to an
 * outside provider, kept seven days and read on Settings › Integrations ›
 * Outside Calls. 🔨 A row carries no key, no token and no secret, and no
 * body but a Zoho JSON answer (decision 12420).
 */

/**
 * Which host answered: Zoho Books' API, Zoho Inventory's API or Zoho's sign-in
 * host, and since decision 5880 every other outside provider by its name.
 * `GoogleMaps` carries geocoding, distance, address validation and the static
 * map; `OpenStreetMap` carries Nominatim and the routing table.
 */
export const OUTSIDE_CALL_PROVIDERS = [
  'ZohoBooks', 'ZohoInventory', 'ZohoAccounts', 'ZohoCrm', 'ZohoExpense', 'Twilio', 'Stripe', 'Plaid', 'Anthropic', 'PeopleDataLabs', 'GoogleMaps', 'GoogleVision', 'Usps',
  'FirebaseMessaging', 'OpenStreetMap', 'CensusGeocoder', 'SingleSignOn', 'Turnstile', 'Geoapify',
] as const;
export type OutsideCallProvider = (typeof OUTSIDE_CALL_PROVIDERS)[number];

export function isOutsideCallProvider(value: unknown): value is OutsideCallProvider {
  return (OUTSIDE_CALL_PROVIDERS as readonly unknown[]).includes(value);
}

/** 🔒 How long a row is kept before the nightly purge deletes it. */
export const OUTSIDE_CALL_LOG_DAYS = 7;
/** 🔒 The refusal's words are cut to this many characters (decision 7370: the Outside Calls page shows all of them). */
export const OUTSIDE_CALL_REFUSAL_MAX = 500;
/** 🔒 The most rows one read of the Outside Calls page answers. */
export const OUTSIDE_CALLS_LIMIT = 500;
/** 🔒 A kept answer is cut to this many characters, with a note of its full length (decision 12420). */
export const OUTSIDE_CALL_ANSWER_MAX = 262_144;
/**
 * 🔒 The providers whose JSON answer a row keeps (decision 12420): the import's documents are read from them.
 * 🔨 Never `ZohoAccounts`, whose answer is a token, and never another provider, whose answers carry people's details.
 */
export const OUTSIDE_CALL_ANSWER_PROVIDERS: readonly OutsideCallProvider[] = ['ZohoBooks', 'ZohoInventory', 'ZohoCrm', 'ZohoExpense'];

export function answerKept(provider: OutsideCallProvider): boolean {
  return OUTSIDE_CALL_ANSWER_PROVIDERS.includes(provider);
}

/**
 * 🔒 An answer as a row keeps it: whole up to the cap, or cut there with ` … [cut: N characters]`, N its full length.
 * 🔨 The cut never splits a surrogate pair (found in review, 2026-11-01): a character outside the basic plane at the cap
 * is dropped whole, so the kept text is valid UTF-16 and the row's write cannot refuse it.
 */
export function keptAnswer(text: string): string {
  if (text.length <= OUTSIDE_CALL_ANSWER_MAX) return text;
  const last = text.charCodeAt(OUTSIDE_CALL_ANSWER_MAX - 1);
  const end = last >= 0xd800 && last <= 0xdbff ? OUTSIDE_CALL_ANSWER_MAX - 1 : OUTSIDE_CALL_ANSWER_MAX;
  return `${text.slice(0, end)} … [cut: ${text.length} characters]`;
}

/** 🔒 The purge, on the job clock under its own hour key, seeded at 3 with the other purges. */
export const PURGE_OUTSIDE_CALL_LOG_JOB = 'purgeOutsideCallLog';
export const PURGE_OUTSIDE_CALL_LOG_HOUR_KEY = 'outsideCalls.purgeRunHour';
export const PURGE_OUTSIDE_CALL_LOG_DEFAULT_HOUR = 3;

/** What a call came to, read from its status and its refusal; the Outside Calls page's status filter offers these three. */
export const OUTSIDE_CALL_STATES = ['answered', 'refused', 'unanswered'] as const;
export type OutsideCallState = (typeof OUTSIDE_CALL_STATES)[number];

export function isOutsideCallState(value: unknown): value is OutsideCallState {
  return (OUTSIDE_CALL_STATES as readonly unknown[]).includes(value);
}

/** No status is no answer; a status of 400 or more, or a refusal under a success status, is refused; anything else answered. */
export function outsideCallState(row: { status: number | null; refusal: string | null }): OutsideCallState {
  if (row.status === null) return 'unanswered';
  if (row.status >= 400 || row.refusal !== null) return 'refused';
  return 'answered';
}

/** One row as the Outside Calls page reads it. */
export interface OutsideCallRow {
  id: string;
  calledAt: string;
  provider: OutsideCallProvider;
  method: string;
  path: string;
  status: number | null;
  durationMs: number;
  bytesRead: number | null;
  importRunId: string | null;
  jobName: string | null;
  refusal: string | null;
  /** 🔒 A low-confidence address answer the call held, as the service, its percent and the reason; never the address (decision 7430). */
  note: AddressConfidenceNote | null;
  /** 🔒 The JSON answer a Zoho call kept (decision 12420); sent only on a read that asks for answers, null where none was kept. */
  answer?: string | null;
}

/** A run the week's rows name, for the run filter: its block and when it started. */
export interface OutsideCallRun {
  id: string;
  block: string;
  startedAt: string;
}

export interface OutsideCallsAnswer {
  rows: OutsideCallRow[];
  /** How many rows the filter matched; more than `rows.length` where the limit cut the list. */
  matched: number;
  limit: number;
  runs: OutsideCallRun[];
}

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const ZOHO_BOOKS_CALLS_PATH = `${ZOHO_BOOKS_IMPORT_PATH}/calls`;

/** 🔒 The read's answers parameter (decision 12420): `1` adds each row's kept answer, which the list's own read never carries. */
export const OUTSIDE_CALLS_ANSWERS_PARAM = 'answers';
/** The read's one-row parameter: the row's window reads its own call with its answer. */
export const OUTSIDE_CALLS_CALL_PARAM = 'call';

export function zohoBooksCallsPath(filter: { run?: string | null; status?: string | null; provider?: string | null; call?: string | null; answers?: boolean } = {}): string {
  const query = new URLSearchParams();
  if (filter.run) query.set('run', filter.run);
  if (filter.status) query.set('status', filter.status);
  if (filter.provider) query.set('provider', filter.provider);
  if (filter.call) query.set(OUTSIDE_CALLS_CALL_PARAM, filter.call);
  if (filter.answers) query.set(OUTSIDE_CALLS_ANSWERS_PARAM, '1');
  const text = query.toString();
  return text ? `${ZOHO_BOOKS_CALLS_PATH}?${text}` : ZOHO_BOOKS_CALLS_PATH;
}

/** The run filter's address parameter on the Outside Calls page. */
export const ZOHO_BOOKS_CALLS_RUN_PARAM = 'run';

/** The provider filter's address parameter on the Outside Calls page, which an integration's card sets. */
export const OUTSIDE_CALLS_PROVIDER_PARAM = 'provider';

/** 🔒 Decision 7370: Settings › Integrations › Outside Calls, a page of its own that every integration's card links to. */
export const OUTSIDE_CALLS_ROUTE = '/settings/system/outside-calls';

/** The page's address, narrowed to a run or to one provider where either is named. */
export function outsideCallsRoute(filter: { run?: string | null; provider?: string | null } = {}): string {
  const query = new URLSearchParams();
  if (filter.run) query.set(ZOHO_BOOKS_CALLS_RUN_PARAM, filter.run);
  if (filter.provider) query.set(OUTSIDE_CALLS_PROVIDER_PARAM, filter.provider);
  const text = query.toString();
  return text ? `${OUTSIDE_CALLS_ROUTE}?${text}` : OUTSIDE_CALLS_ROUTE;
}

/** 🔒 Where an import run's report opens its calls: the Outside Calls page, filtered to that run. */
export function zohoBooksRunCallsRoute(runId: string): string {
  return outsideCallsRoute({ run: runId });
}

/** Whether a run started recently enough that its calls are still kept. */
export function runCallsKept(startedAt: string | Date, now: Date = new Date()): boolean {
  const at = typeof startedAt === 'string' ? Date.parse(startedAt) : startedAt.getTime();
  return Number.isFinite(at) && now.getTime() - at < OUTSIDE_CALL_LOG_DAYS * 86_400_000;
}
